import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { scrollToId } from "@/components/site/SmoothScroll";

const ANCHORS = [
  { label: "Approach", href: "#belief", key: "approach", anchor: "belief" },
  { label: "Portfolio", href: "#portfolio", key: "portfolio", anchor: "portfolio" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("approach");
  const location = useLocation();
  const navigate = useNavigate();
  const onFounder = location.pathname === "/founder";
  const onOperators = location.pathname.startsWith("/operators");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.75);
      if (location.pathname !== "/") return;
      const y = window.scrollY + window.innerHeight * 0.4;
      let cur = "approach";
      ANCHORS.forEach((l) => {
        const el = document.getElementById(l.anchor);
        if (el && el.offsetTop <= y) cur = l.key;
      });
      const connectEl = document.getElementById("connect");
      if (connectEl && connectEl.offsetTop <= y) cur = "connect";
      setActive(cur);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const goAnchor = (e, href) => {
    e.preventDefault();
    const id = href.replace("#", "");
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id, { offset: -80 });
    }
  };

  const goTop = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") navigate("/");
    else if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const linkClass = (isActive) =>
    `group relative hidden text-[0.7rem] font-medium uppercase tracking-[0.2em] transition-colors duration-300 sm:inline-block ${
      isActive ? "text-cream" : "text-cream/55 hover:text-cream"
    }`;

  const underline = (isActive) =>
    `absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${
      isActive ? "w-full" : "w-0 group-hover:w-full"
    }`;

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled || onFounder
          ? "border-b border-white/10 bg-noir/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12 md:py-5">
        <a
          data-testid="nav-logo"
          href="/"
          onClick={goTop}
          className="inline-flex items-center transition-opacity duration-300 hover:opacity-80"
        >
          <img
            src="/gv-logo-light.png"
            alt="Govil Ventures"
            className="h-6 w-auto md:h-7"
          />
        </a>

        <div className="flex items-center gap-7 md:gap-11">
          {ANCHORS.map((l) => {
            const isActive = location.pathname === "/" && active === l.key;
            return (
              <a
                key={l.label}
                data-testid={`nav-link-${l.key}`}
                href={l.href}
                onClick={(e) => goAnchor(e, l.href)}
                className={linkClass(isActive)}
              >
                {l.label}
                <span className={underline(isActive)} />
              </a>
            );
          })}
          <Link
            to="/operators"
            data-testid="nav-link-operators"
            className={linkClass(onOperators)}
          >
            Operators
            <span className={underline(onOperators)} />
          </Link>
          <Link
            to="/founder"
            data-testid="nav-link-founder"
            className={linkClass(onFounder)}
          >
            Founder
            <span className={underline(onFounder)} />
          </Link>
          <a
            data-testid="nav-link-connect"
            href="#connect"
            onClick={(e) => goAnchor(e, "#connect")}
            className={linkClass(location.pathname === "/" && active === "connect")}
          >
            Connect
            <span className={underline(location.pathname === "/" && active === "connect")} />
          </a>
        </div>
      </nav>
    </motion.header>
  );
}
